import { useEffect } from "react";

const MOBILE_CHROME_MQ = "(max-width: 968px)";

/**
 * Keeps `.app-content` sized to the visible viewport on mobile/tablet and
 * recovers layout after the on-screen keyboard opens/closes (iOS/Android).
 */
export function useMobileViewportChromeFix() {
    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const scroller = document.querySelector(".app-content");
        const visualViewport = window.visualViewport;
        if (!scroller || !visualViewport) return undefined;

        const isMobileChrome = () => window.matchMedia(MOBILE_CHROME_MQ).matches;

        let lastViewportHeight = visualViewport.height;
        let rafId = 0;

        const clearScrollerInlineSize = () => {
            scroller.style.removeProperty("height");
            scroller.style.removeProperty("min-height");
        };

        const syncScrollerToViewport = () => {
            if (!isMobileChrome()) {
                clearScrollerInlineSize();
                return;
            }

            const nextHeight = Math.round(visualViewport.height);
            scroller.style.height = `${nextHeight}px`;

            // iOS Safari can leave the page offset after the keyboard dismisses.
            if (visualViewport.offsetTop > 0) {
                window.scrollTo(0, 0);
            }

            const keyboardLikelyClosed = nextHeight > lastViewportHeight + 48;
            if (keyboardLikelyClosed) {
                const header = document.querySelector(".restaurant-site-header.home-ph-header");
                if (header) {
                    const headerRect = header.getBoundingClientRect();
                    const scrollerRect = scroller.getBoundingClientRect();
                    if (headerRect.bottom < scrollerRect.top + 2) {
                        scroller.scrollTop = 0;
                    }
                }
            }

            lastViewportHeight = nextHeight;
        };

        const scheduleSync = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(syncScrollerToViewport);
        };

        const onFocusOut = (event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) return;
            if (!target.matches("input, textarea, select")) return;
            window.setTimeout(scheduleSync, 60);
            window.setTimeout(scheduleSync, 320);
        };

        const onMediaChange = () => scheduleSync();

        visualViewport.addEventListener("resize", scheduleSync);
        visualViewport.addEventListener("scroll", scheduleSync);
        window.addEventListener("orientationchange", scheduleSync);
        document.addEventListener("focusout", onFocusOut, true);

        const mediaQuery = window.matchMedia(MOBILE_CHROME_MQ);
        if (typeof mediaQuery.addEventListener === "function") {
            mediaQuery.addEventListener("change", onMediaChange);
        } else {
            mediaQuery.addListener(onMediaChange);
        }

        scheduleSync();

        return () => {
            cancelAnimationFrame(rafId);
            visualViewport.removeEventListener("resize", scheduleSync);
            visualViewport.removeEventListener("scroll", scheduleSync);
            window.removeEventListener("orientationchange", scheduleSync);
            document.removeEventListener("focusout", onFocusOut, true);
            if (typeof mediaQuery.removeEventListener === "function") {
                mediaQuery.removeEventListener("change", onMediaChange);
            } else {
                mediaQuery.removeListener(onMediaChange);
            }
            clearScrollerInlineSize();
        };
    }, []);
}

export default useMobileViewportChromeFix;
